import React, { useState, useEffect } from 'react';
import { FormControl, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { languageManager, type Language } from '@/Utils/languageManager';
import { stripLanguagePrefix, addLanguagePrefix } from '@/Utils/routing';

export type LanguageCode = Language;

interface LanguageOption {
    code: LanguageCode;
    label: string;
    flag: string;
}

const languages: LanguageOption[] = [
    { code: 'id', label: 'Bahasa Indonesia', flag: '🇮🇩' },
    { code: 'en', label: 'English',          flag: '🇺🇸' },
    { code: 'ko', label: '한국어',            flag: '🇰🇷' },
    { code: 'vn', label: 'Tiếng Việt',       flag: '🇻🇳' }
];

interface LanguageSelectorProps {
    onChange?: (language: LanguageCode) => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ onChange }) => {
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(languageManager.getLanguage());

    useEffect(() => {
        // Subscribe to language changes
        const unsubscribe = languageManager.subscribe((lang) => {
            setSelectedLanguage(lang);
        });
        return unsubscribe;
    }, []);

    const handleChange = (event: SelectChangeEvent) => {
        const languageCode = event.target.value as LanguageCode;
        setSelectedLanguage(languageCode);

        // Update URL with new language prefix
        const currentPath = window.location.pathname;
        const strippedPath = stripLanguagePrefix(currentPath);
        const newPath = addLanguagePrefix(strippedPath, languageCode);

        // Update URL and sync language manager
        window.history.pushState({}, '', newPath);
        languageManager.syncFromUrl();

        // Trigger popstate to update App routing
        window.dispatchEvent(new PopStateEvent('popstate'));

        if (onChange) {
            onChange(languageCode);
        }
    };

    return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
                value={selectedLanguage}
                onChange={handleChange}
                displayEmpty
                sx={{
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #e0e0e0'
                    }
                }}
            >
                {languages.map((language) => (
                    <MenuItem key={language.code} value={language.code}>
                        <span style={{ marginRight: '8px' }}>{language.flag}</span>
                        {language.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default LanguageSelector;

export const getStoredLanguage = (): LanguageCode => {
    return languageManager.getLanguage();
};

export const setStoredLanguage = (language: LanguageCode): void => {
    // Update URL with new language prefix
    const currentPath = window.location.pathname;
    const strippedPath = stripLanguagePrefix(currentPath);
    const newPath = addLanguagePrefix(strippedPath, language);

    window.history.pushState({}, '', newPath);
    languageManager.syncFromUrl();
    window.dispatchEvent(new PopStateEvent('popstate'));
};